import React, { useRef, useState, useEffect } from "react";
import { PDFDocument } from "pdf-lib";
import SignaturePad from "signature_pad";
import "./ContratoTab.css";

export default function ConsentimientoMenoresTab({ paciente }) {
  const canvasRef = useRef(null);
  const [signaturePad, setSignaturePad] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [selectedSpec, setSelectedSpec] = useState("");
  const [availableSpecs, setAvailableSpecs] = useState([]);

  // 🧠 Cargar especialidades del paciente
  useEffect(() => {
    if (!paciente?.servicios) return;
    const serviciosArr = Array.isArray(paciente.servicios)
      ? paciente.servicios
      : String(paciente.servicios)
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);

    setAvailableSpecs(serviciosArr);
    if (serviciosArr.length === 1) setSelectedSpec(serviciosArr[0]);
  }, [paciente]);

  // 🖋️ Inicializar firma
  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    canvas.width = 350;
    canvas.height = 150;
    const pad = new SignaturePad(canvas, { backgroundColor: "#fff", penColor: "#000" });
    setSignaturePad(pad);
    return () => pad.off();
  }, [fileUrl]); // 👈 se reinicia cuando cambia el PDF

  // 🧾 Obtener consentimiento PDF
  useEffect(() => {
    if (!paciente?.id || !selectedSpec) return;
    setLoading(true);
    setError("");
    setFileUrl(null);
    fetch(`https://te-ayudo.es/back/api/generate_consent.php?id=${paciente.id}&specialty=${selectedSpec}`)
      .then(async (res) => {
        const text = await res.text();
        try {
          return JSON.parse(text);
        } catch {
          throw new Error("Respuesta del backend no es JSON válido");
        }
      })
      .then((data) => {
        if (data.ok && data.pdf_local) setFileUrl(data.pdf_local);
        else throw new Error(data.error || "Error al generar consentimiento");
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [paciente?.id, selectedSpec]);

  // 🧹 Limpiar firma
  const handleClear = () => signaturePad?.clear();

const handleSave = async () => {
  if (!signaturePad || signaturePad.isEmpty()) {
    alert("Por favor, firma antes de guardar.");
    return;
  }
  if (!fileUrl) {
    alert("No se ha podido cargar el consentimiento.");
    return;
  }

  try {
    setUploading(true);
    setMessage("");

    // 📄 1. Cargar y firmar el PDF
    const pdfBytes = await fetch(fileUrl).then((res) => res.arrayBuffer());
    const pdfDoc = await PDFDocument.load(pdfBytes);
    const pngBytes = await fetch(signaturePad.toDataURL()).then((res) => res.arrayBuffer());
    const pngImage = await pdfDoc.embedPng(pngBytes);

    // 🔧 POSICIÓN Y TAMAÑO DE LA FIRMA (ajustado)
    const page = pdfDoc.getPages().at(-1);
    const { width, height } = page.getSize();

   const MARGIN_LEFT = width - 260; // moverla a la derecha del todo
    const MARGIN_BOTTOM = 70; // cerca del pie
    const BOX_W = 220;        // ancho máximo
    const BOX_H = 90;         // alto máximo

    const pngDims = pngImage.scale(1);
    const scale = Math.min(BOX_W / pngDims.width, BOX_H / pngDims.height);
    const drawW = pngDims.width * scale;
    const drawH = pngDims.height * scale;

    page.drawImage(pngImage, {
      x: MARGIN_LEFT,
      y: MARGIN_BOTTOM,
      width: drawW,
      height: drawH,
    });

    const finalPdf = await pdfDoc.save();
    const blob = new Blob([finalPdf], { type: "application/pdf" });

    // 📤 2. Subir a Drive
    const formData = new FormData();
    formData.append("file", blob, `consentimiento_firmado_${paciente.id}_${selectedSpec}.pdf`);
    formData.append("patient_id", paciente.id);
    formData.append("type", "consentimiento");
    formData.append("specialty", selectedSpec);

    const res = await fetch("https://te-ayudo.es/back/api/upload_drive.php", {
      method: "POST",
      body: formData,
    });
    const data = await res.json();
    if (!data.ok) throw new Error(data.error);

    setUploadedFile({
      webViewLink: data.file?.webViewLink || null,
      pdf_local: data.pdf_local || null,
    });

    // ✉️ 3. Enviar correo con el PDF adjunto
    const mailRes = await fetch("https://te-ayudo.es/back/api/send_consent_mail.php", {
      method: "POST",
      body: formData,
    });
    const mailData = await mailRes.json();

    if (mailData.ok) {
      setMessage("✅ Consentimiento firmado, subido y enviado por correo correctamente.");
    } else {
      setMessage("⚠️ Consentimiento subido, pero error al enviar correo: " + mailData.error);
    }
  } catch (err) {
    setMessage("❌ " + err.message);
  } finally {
    setUploading(false);
  }
};

  return (
    <div className="contrato-tab">
      <h3>🧾 Consentimiento expreso de menores</h3>

      {/* Selector de especialidad */}
      {availableSpecs.length > 0 && (
        <div className="form-row">
          <label>Especialidad / Servicio</label>
          <select value={selectedSpec} onChange={(e) => setSelectedSpec(e.target.value)}>
            <option value="">-- Selecciona un servicio --</option>
            {availableSpecs.map((s) => (
              <option key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>
        </div>
      )}

      {loading && <p>Generando consentimiento...</p>}
      {error && <p style={{ color: "red" }}>❌ {error}</p>}
      {!loading && fileUrl && (
        <>
          <iframe
            src={fileUrl}
            className="contrato-preview"
            title="Vista previa consentimiento"
            style={{ width: "100%", height: "800px", border: "none" }}
          ></iframe>
        </>
      )}

      {/* Firma digital */}
      {fileUrl && (
        <div className="signature-section">
          <h4>✍️ Firma del tutor legal</h4>
          <div className="signature-box">
            <canvas ref={canvasRef} width={350} height={150} className="signature-canvas" />
          </div>
          <div className="signature-actions">
            <button onClick={handleClear}>Borrar firma</button>
            <button onClick={handleSave} disabled={uploading}>
              {uploading ? "Guardando..." : "Guardar consentimiento firmado"}
            </button>
          </div>
        </div>
      )}

      {message && (
        <div className="message-box">
          <p>{message}</p>
          {uploadedFile && (
            <button
              className="btn-ver-contrato"
              onClick={() => {
                const url = uploadedFile.pdf_local || uploadedFile.webViewLink;
                if (url) window.open(url, "_blank");
              }}
            >
              📄 Ver consentimiento firmado
            </button>
          )}
        </div>
      )}
    </div>
  );
}
